'use client';
import { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import LoadingDots from '@/components/ui/LoadingDots';
import ProfileAvatar from '@/components/ui/ProfileUser/ProfileAvatar';
import { AlertTriangle, Upload } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { analytics } from '@/lib/analytics';
import { processImage } from '@/utils/imageProcessor';
import { stageFile } from '@/utils/uploads/stage-file';

const ProfileAvatarUploader = ({
  avatarUrl,
  username,
  userId,
}: {
  avatarUrl: string;
  username: string;
  userId: string;
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const router = useRouter();

  const uploadAvatar = async (file: File) => {
    setIsUploading(true);
    try {
      const staged = await stageFile(file, 'avatar');
      await staged.done;

      const res = await fetch('/api/users/avatar', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ uploadId: staged.uploadId }),
      });
      if (!res.ok) {
        const { error: updateError } = await res.json().catch(() => ({ error: res.statusText }));
        setError(`Error updating avatar: ${updateError}`);
        return;
      }
      router.refresh();
    } catch (error) {
      setError(`Error uploading avatar: ${error}`);
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/jpeg': ['.jpg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
      'image/heic': ['.heic'],
      'image/heif': ['.heif'],
    },
    // maxSize: 500 * 1024, // 200KB in bytes
    onDrop: async (acceptedFiles, rejectedFiles) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        // Check if the rejection was due to file size
        if (rejectedFiles[0].errors[0]?.code === 'file-too-large') {
          setError('Must be less than 500KB');
          return;
        }
        setError('File type not supported.');
        return;
      }

      if (acceptedFiles.length === 0) return;

      analytics.capture('profile.avatar.uploaded', {
        from: 'profile_avatar_uploader',
      });

      setIsUploading(true);
      const acceptedFile = await processImage(acceptedFiles[0]);
      if (!acceptedFile) {
        setError('Error processing image');
        setIsUploading(false);
        return;
      }

      await uploadAvatar(acceptedFile);
      setIsUploading(false);
    },
  });

  return (
    <div className="relative">
      <div {...getRootProps()} className="relative cursor-pointer">
        <input {...getInputProps()} />
        <div className={`relative ${isDragActive ? 'opacity-50' : ''}`}>
          <ProfileAvatar avatarUrl={avatarUrl} ownProfile={true} username={username} />
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
              <LoadingDots />
            </div>
          )}
        </div>
        <div
          className="absolute z-1 bottom-0 right-0 top-0 left-0 flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity duration-200 rounded-full"
          style={{
            backgroundColor: '#151517',
          }}
        >
          <span
            style={{
              borderRadius: '100px',
              padding: '0 14px',
              height: '50px',
            }}
            className="flex-row gap-3 items-center border border-white text-white w-fit flex"
          >
            <Upload size={20} />
            Upload Photo
          </span>
        </div>
      </div>
      {error && (
        <div className="absolute mt-2 text-sm text-zinc-400 flex flex-row gap-2 items-center">
          <AlertTriangle className="w-4 h-4 text-red-500" />
          {error}
        </div>
      )}
    </div>
  );
};

export default ProfileAvatarUploader;
